// @ts-nocheck
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './hooks/useHotelPartnerId';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Users, ClipboardList, AlertTriangle, Building2 } from 'lucide-react';

const HotelDashboard: React.FC = () => {
  const { data: partnerId } = useHotelPartnerId();
  const navigate = useNavigate();

  const { data: assignmentCount, isLoading: loadingAssignments } = useQuery({
    queryKey: ['hotel-dash-assignments', partnerId],
    queryFn: async () => {
      const { count, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id', { count: 'exact', head: true })
        .eq('hotel_partner_user_id', partnerId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!partnerId,
  });

  const { data: complianceRows, isLoading: loadingCompliance } = useQuery({
    queryKey: ['hotel-dash-compliance', partnerId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('event_id, unbooked_teams, attrition_risk, unlinked_blocks')
        .eq('hotel_partner_user_id', partnerId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!partnerId,
  });

  const { data: actionCount, isLoading: loadingActions } = useQuery({
    queryKey: ['hotel-dash-actions', partnerId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .rpc('rle_hotel_unbooked_teams_past_days', { p_days: 7, p_partner_user_id: partnerId });
      if (error) throw error;
      return data?.length ?? 0;
    },
    enabled: !!partnerId,
  });

  const { data: hotelCount, isLoading: loadingHotels } = useQuery({
    queryKey: ['hotel-dash-hotels', partnerId],
    queryFn: async () => {
      const { data: assignments } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id')
        .eq('hotel_partner_user_id', partnerId);
      if (!assignments || assignments.length === 0) return 0;
      const ids = assignments.map((a) => a.id);
      const { count, error } = await (supabase as any)
        .from('hotel_properties')
        .select('id', { count: 'exact', head: true })
        .in('hotel_assignment_id', ids);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!partnerId,
  });

  const unbookedTeams = complianceRows?.reduce((sum: number, r: any) => sum + (r.unbooked_teams ?? 0), 0) ?? 0;
  const hasAttritionRisk = complianceRows?.some((r: any) => r.attrition_risk) ?? false;
  const unlinkedBlocks = complianceRows?.reduce((sum, r) => sum + Number(r.unlinked_blocks || 0), 0) ?? 0;
  const firstUnlinkedEvent = complianceRows?.find(r => Number(r.unlinked_blocks) > 0)?.event_id;

  const stats = [
    {
      title: 'Unlinked blocks', value: unlinkedBlocks, loading: loadingCompliance, icon: ClipboardList,
      onClick: () => navigate(firstUnlinkedEvent ? `/hotel-portal/events/${firstUnlinkedEvent}` : '/hotel-portal/tracker'),
    },
    {
      title: 'Assigned Events',
      value: assignmentCount,
      loading: loadingAssignments,
      icon: CalendarDays,
      onClick: () => navigate('/hotel-portal/events'),
    },
    {
      title: 'Hotels Managed',
      value: hotelCount,
      loading: loadingHotels,
      icon: Building2,
      onClick: () => navigate('/hotel-portal/hotels'),
    },
    {
      title: 'Unbooked Teams',
      value: unbookedTeams,
      loading: loadingCompliance,
      icon: Users,
      onClick: () => navigate('/hotel-portal/tracker'),
    },
    {
      title: 'Action Required',
      value: actionCount,
      loading: loadingActions,
      icon: ClipboardList,
      onClick: () => navigate('/hotel-portal/tracker'),
      highlight: (actionCount ?? 0) > 0,
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.title}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={s.onClick}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {s.title}
              </CardTitle>
              <s.icon className="h-5 w-5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {s.loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <p className={`text-3xl font-bold ${s.highlight ? 'text-destructive' : ''}`}>
                  {s.value}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {hasAttritionRisk && (
        <Card className="border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600 shrink-0" />
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              One or more events are at attrition risk — review your contracts soon.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HotelDashboard;
